import {Component, Input, OnDestroy, OnInit,} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR, ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import {ValidationService} from '../../../../services/web/validation.service';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import { TimeUnit } from 'src/app/models/web/time-unit.enum';

@Component({
    selector: 'app-date-range',
    templateUrl: 'date-range.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: DateRangeComponent
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: DateRangeComponent
        }
    ],
    standalone: false
})

export class DateRangeComponent implements ControlValueAccessor, OnInit, OnDestroy, Validator{
  public optionalStartBackingField: boolean;
  @Input()
  public get optionalStart() {
    return this.optionalStartBackingField;
  }
  public set optionalStart(value: any) {
    this.optionalStartBackingField = this.coerceBooleanProperty(value);
  }
  public optionalEndBackingField: boolean;
  @Input()
  public get optionalEnd() {
    return this.optionalEndBackingField;
  }
  public set optionalEnd(value: any) {
    this.optionalEndBackingField = this.coerceBooleanProperty(value);
  }

  @Input() public requiredStart: boolean = false;
  @Input() public maxRange: number = null;
  @Input() public maxRangeTimeUnit: TimeUnit = null;
  public dateRangeForm: FormGroup;
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder
  ){ }

  public onTouched = () => {};

  public coerceBooleanProperty(value: any): boolean {
    return value != null && `${value}` !== 'false';
  }

  public ngOnInit(): void {
    this.dateRangeForm = this.formBuilder.group({
      startDate: [''],
      endDate: ['']
    });

    // endDate validators
    let endDateValidators = [];
    if(!this.optionalEnd){
      endDateValidators.push(Validators.required);
    }

    if(this.maxRange !== null && this.maxRange > 0){
      endDateValidators.push(
        ValidationService.maxRangeValidator.bind(this, this.dateRangeForm.get('startDate'), this.dateRangeForm.get('endDate'), this.maxRange, this.maxRangeTimeUnit)
      )
    }

    this.dateRangeForm.get('endDate').setValidators(endDateValidators);

    // startDate validators
    let startDateValidators = [];
    if (!this.optionalStart) {
      startDateValidators.push(Validators.required);
    }
    startDateValidators.push(
      ValidationService.startDateValidator.bind(this, this.dateRangeForm.get('startDate'), this.dateRangeForm.get('endDate'))
    );
    this.dateRangeForm.get('startDate').setValidators(startDateValidators);

    this.dateRangeForm.get('endDate').valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.dateRangeForm.get('startDate').updateValueAndValidity({onlySelf: true, emitEvent: false}));

      this.dateRangeForm.get('startDate').valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.dateRangeForm.get('endDate').updateValueAndValidity({onlySelf: true, emitEvent: false}));
  }
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public registerOnChange(fn: any): void {
    if(this.dateRangeForm !== undefined) {
      this.dateRangeForm.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(fn);
    }
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public writeValue(obj: any): void {
    if(obj !== undefined && obj !== null) {
      this.dateRangeForm.setValue(obj, {emitEvent: false});
    }
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    if(this.dateRangeForm.valid){return null;}

    const errors: any = {};
    const startDateErrors = this.dateRangeForm.controls.startDate.errors;
    const endDateErrors = this.dateRangeForm.controls.endDate.errors;

    if(startDateErrors) {
      errors.startDate = startDateErrors;
    }

    if(endDateErrors) {
      errors.endDate = endDateErrors;
    }

    return errors;
  }
}
